import { MapPin, Phone, MessageCircle, Clock } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { CTAButtons } from "@/components/CTAButtons";
import { ADDRESS_LINES, MAP_EMBED_URL, PHONE_DISPLAY, PHONE_NUMBER, WHATSAPP_NUMBER, TIMINGS } from "@/data/clinic";

const InfoRow = ({ icon: Icon, title, children, testId }) => (
  <div className="flex items-start gap-4 border-t border-[#063A2F]/10 py-5" data-testid={testId}>
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#F2FBF7] text-[#0D9488]">
      <Icon className="h-5 w-5" strokeWidth={1.5} />
    </span>
    <div>
      <h3 className="text-sm font-bold text-[#063A2F]">{title}</h3>
      <div className="mt-1 text-sm leading-relaxed text-[#475569]">{children}</div>
    </div>
  </div>
);

export const Contact = () => (
  <section id="contact" data-testid="contact-section" className="bg-[#FDFCF8] py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal className="max-w-2xl">
        <Eyebrow>Location &amp; Contact</Eyebrow>
        <h2 className="font-display mt-5 text-4xl tracking-tight text-[#063A2F] sm:text-5xl">
          Find us in <span className="italic text-[#0D9488]">Bahadurpura</span>
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-10 lg:grid-cols-2">
        <Reveal>
          <div className="rounded-3xl border border-[#063A2F]/10 bg-white p-8 shadow-[0_8px_30px_rgba(6,58,47,0.06)] lg:p-10">
            <InfoRow icon={MapPin} title="Clinic Address" testId="contact-address">
              {ADDRESS_LINES.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </InfoRow>
            <InfoRow icon={Phone} title="Call Clinic" testId="contact-phone">
              <a
                href={`tel:${PHONE_NUMBER}`}
                data-testid="contact-phone-link"
                className="font-semibold text-[#0D9488] transition-colors duration-200 hover:text-[#063A2F]"
              >
                {PHONE_DISPLAY}
              </a>
            </InfoRow>
            <InfoRow icon={MessageCircle} title="WhatsApp Clinic" testId="contact-whatsapp">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="font-semibold text-[#0D9488] transition-colors duration-200 hover:text-[#063A2F]"
              >
                {PHONE_DISPLAY}
              </a>
            </InfoRow>
            <div className="border-t border-[#063A2F]/10 py-5" data-testid="contact-timings">
              <div className="flex items-center gap-4">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#F2FBF7] text-[#0D9488]">
                  <Clock className="h-5 w-5" strokeWidth={1.5} />
                </span>
                <h3 className="text-sm font-bold text-[#063A2F]">Clinic Timings</h3>
              </div>
              <div className="mt-4 sm:pl-14">
                {TIMINGS.map((t) => (
                  <div
                    key={t.day}
                    className="flex flex-col gap-y-1 border-b border-[#063A2F]/10 py-2.5 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-x-6"
                  >
                    <span className="text-sm font-semibold text-[#063A2F]">{t.day}</span>
                    {t.closed ? (
                      <span className="self-start rounded-full bg-[#D4AF37]/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[#8A6D1F] sm:self-auto">
                        By Appointment Only
                      </span>
                    ) : (
                      <span className="flex flex-col text-sm leading-relaxed text-[#475569] sm:text-right">
                        {t.hours.map((h) => (
                          <span key={h}>{h}</span>
                        ))}
                      </span>
                    )}
                  </div>
                ))}
                <p className="mt-3 text-xs leading-relaxed text-[#475569]/80">
                  Walk-ins are welcome during clinic hours — patients are attended on a
                  first-come, first-served basis.
                </p>
              </div>
            </div>
            <div className="mt-8">
              <CTAButtons />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="h-full">
          <div className="relative h-full min-h-[420px] overflow-hidden rounded-3xl border border-[#D4AF37]/40 bg-white shadow-[0_8px_30px_rgba(6,58,47,0.06)]">
            <iframe
              title="Smart Dental Clinic — Google Map"
              data-testid="clinic-map"
              src={MAP_EMBED_URL}
              className="absolute inset-0 h-full w-full border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
            <span className="absolute left-5 top-5 rounded-full border border-[#D4AF37]/40 bg-[#FDFCF8] px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#0F574E] shadow-sm">
              Google Maps
            </span>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
